import { useState, useEffect } from "react";
import { Input } from "./ui/input";
import { Pipette } from "lucide-react";

function isValidHex(hex) {
  return /^#([0-9A-Fa-f]{6})$/.test(hex);
}

/**
 * A colour swatch that opens the browser's own spectrum dialog, beside a hex
 * field for typing one in exactly.
 *
 * There is deliberately no grid of preset swatches. A palette is a menu of
 * somebody else's opinions, and the native dialog already offers the whole
 * spectrum plus an eyedropper — the control everybody already knows from every
 * other program.
 *
 * The raw <input type="color"> renders differently in every browser, so it sits
 * invisible over a styled tile that shows the current colour and takes the
 * click.
 */
export function ColorPicker({ value, onChange, label }) {
  const [hexInput, setHexInput] = useState(value || "#3B82F6");

  useEffect(() => {
    setHexInput(value || "#3B82F6");
  }, [value]);

  const handleHexChange = (e) => {
    let val = e.target.value;
    if (!val.startsWith("#")) val = "#" + val;
    setHexInput(val);
    if (isValidHex(val)) onChange(val.toUpperCase());
  };

  const handleHexBlur = () => {
    if (!isValidHex(hexInput)) setHexInput(value || "#3B82F6");
  };

  const pick = (color) => {
    setHexInput(color.toUpperCase());
    onChange(color.toUpperCase());
  };

  const current = isValidHex(hexInput) ? hexInput : value || "#3B82F6";

  return (
    <div className="space-y-2">
      {label && <p className="text-sm font-medium text-slate-700">{label}</p>}
      <div className="flex items-center gap-2">
        <div className="relative shrink-0">
          <input
            type="color"
            value={current}
            onChange={(e) => pick(e.target.value)}
            aria-label={label || "Pick a colour"}
            data-testid="color-native-input"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div
            className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center pointer-events-none shadow-sm"
            style={{ backgroundColor: current }}
          >
            <Pipette className="w-4 h-4 drop-shadow" style={{ color: getContrastText(current) }} />
          </div>
        </div>

        <Input
          value={hexInput}
          onChange={handleHexChange}
          onBlur={handleHexBlur}
          placeholder="#3B82F6"
          maxLength={7}
          className="font-mono text-xs border-slate-200 h-10 uppercase"
          data-testid="color-hex-input"
        />
      </div>
    </div>
  );
}

// Utility: compute relative luminance to determine text contrast
export function getLuminance(hex) {
  if (!hex || !isValidHex(hex)) return 0.5;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const sR = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const sG = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const sB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  return 0.2126 * sR + 0.7152 * sG + 0.0722 * sB;
}

export function getContrastText(hex) {
  return getLuminance(hex) > 0.4 ? "#1e293b" : "#ffffff";
}

export function getContrastMuted(hex) {
  return getLuminance(hex) > 0.4 ? "#475569" : "rgba(255,255,255,0.7)";
}

/**
 * Same colour at a given opacity, as an rgba() string.
 *
 * Used to tint a whole page in a project's own colour without repainting
 * every surface: a few percent of the hue over white keeps text legible while
 * making the page recognisably that project's.
 */
export function hexToRgba(hex, alpha) {
  if (!isValidHex(hex)) return `rgba(148, 163, 184, ${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
